import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Wallet, Lock, Loader2 } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { ASSETS } from '@/assets';
import { PetPeek } from '@/components/PetPeek';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUNDLE_TOTALS = { 1: 20, 2: 38, 3: 54 };
const COUNTRY_CODES = ['ES', 'DE', 'FR', 'NL', 'ROW'];

export const OrderSection = () => {
  const { t } = useLanguage();
  const [quantity, setQuantity] = useState(1);
  const [country, setCountry] = useState('ES');
  const [email, setEmail] = useState('');
  const [method, setMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [paypalCfg, setPaypalCfg] = useState({ enabled: false, client_id: null });
  const [config, setConfig] = useState({ shipping_rates: [], stock: null });
  const paypalContainerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/payments/paypal/config`)
      .then(({ data }) => setPaypalCfg(data))
      .catch(() => setPaypalCfg({ enabled: false, client_id: null }));
    axios.get(`${API}/store/config`)
      .then(({ data }) => setConfig(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (method !== 'paypal' || !paypalCfg.enabled || !paypalCfg.client_id) return;
    const renderButtons = () => {
      if (!window.paypal || !paypalContainerRef.current) return;
      paypalContainerRef.current.innerHTML = '';
      window.paypal.Buttons({
        style: { layout: 'vertical', color: 'gold', shape: 'pill', label: 'paypal' },
        createOrder: async () => {
          const { data } = await axios.post(`${API}/payments/paypal/create-order`, {
            quantity,
            email: email || undefined,
            country,
          });
          return data.order_id;
        },
        onApprove: async (data) => {
          try {
            await axios.post(`${API}/payments/paypal/capture/${data.orderID}`);
            window.location.href = `/payment/success?session_id=${data.orderID}`;
          } catch (err) {
            toast.error(t.order.error);
          }
        },
        onError: () => toast.error(t.order.error),
      }).render(paypalContainerRef.current);
    };
    const existing = document.getElementById('paypal-sdk');
    if (existing) {
      renderButtons();
      return;
    }
    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalCfg.client_id}&currency=EUR&intent=capture`;
    script.onload = renderButtons;
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, quantity, paypalCfg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (method === 'paypal') {
      if (paypalCfg.enabled) return; // PayPal buttons handle the flow
      toast.info(t.order.paypalNote);
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        quantity,
        origin_url: window.location.origin,
        email: email || undefined,
        country,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(t.order.error);
      setLoading(false);
    }
  };

  const showPaypalButtons = method === 'paypal' && paypalCfg.enabled;

  const rate = config.shipping_rates.find((r) => r.route === country)
    || config.shipping_rates.find((r) => r.route === 'ROW');
  const shippingEur = rate ? rate.fee_cents / 100 : 0;
  const bundleTotal = BUNDLE_TOTALS[quantity];
  const totalEur = bundleTotal + shippingEur;
  const oldPrice = quantity * 40;
  const savePct = Math.round((1 - bundleTotal / oldPrice) * 100);
  const fmt = (n) => `€${n.toFixed(2).replace(/\.00$/, '').replace('.', ',')}`;

  return (
    <div className="bg-brand-bone p-8 sm:p-10 rounded-3xl border-2 border-brand-green shadow-2xl relative" data-testid="order-card">
      <PetPeek type="dog" className="-top-7 -left-4 hidden sm:block" />
      <div className="flex items-center justify-between pb-6 border-b-2 border-dashed border-brand-green/20">
        <div className="flex items-center gap-4">
          <img src={ASSETS.bottle} alt="Angry Tangerine concentrate bottle" data-testid="order-bottle-img" className="w-12 h-16 object-contain rounded-lg bg-brand-cream border border-brand-green/10 p-1" />
          <div>
            <h3 className="font-display text-3xl uppercase text-brand-green leading-none">{t.order.title}</h3>
            <p className="text-xs text-brand-green/70 font-bold mt-1.5">{t.order.subtitle}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className="block text-sm font-bold text-brand-green/40 line-through" data-testid="order-old-price">{fmt(oldPrice)}</span>
          <span className="text-3xl font-display text-brand-orange" data-testid="order-price">{fmt(bundleTotal)}</span>
          <span className="inline-block mt-1 text-[10px] font-black uppercase tracking-wider bg-brand-orange text-white px-2 py-0.5 rounded-full" data-testid="order-save-badge">
            {t.order.saveBadge.replace('{p}', savePct)}
          </span>
          <span className="block text-[11px] text-brand-green/60 uppercase font-bold mt-0.5">{t.order.perBottle}</span>
        </div>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-testid="order-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="quantity-select" className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-2">{t.order.qtyLabel}</label>
            <select
              id="quantity-select"
              data-testid="quantity-select"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-full px-4 py-3.5 rounded-2xl border-2 border-brand-green/20 bg-brand-cream text-sm font-bold text-brand-green focus:outline-none focus:border-brand-orange transition-colors"
            >
              {t.order.qtyOptions.map((opt, i) => (
                <option key={opt} value={i + 1}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="country-select" className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-2">{t.order.countryLabel}</label>
            <select
              id="country-select"
              data-testid="country-select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-4 py-3.5 rounded-2xl border-2 border-brand-green/20 bg-brand-cream text-sm font-bold text-brand-green focus:outline-none focus:border-brand-orange transition-colors"
            >
              {t.order.countries.map((c, i) => (
                <option key={c} value={COUNTRY_CODES[i]}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="order-email" className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-2">{t.order.emailLabel}</label>
          <input
            id="order-email"
            type="email"
            data-testid="order-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.order.emailPlaceholder}
            className="w-full px-4 py-3.5 rounded-2xl border-2 border-brand-green/20 bg-white text-sm text-brand-green focus:outline-none focus:border-brand-orange transition-colors"
          />
        </div>

        <div className="pt-1">
          <span className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-2">{t.order.paymentLabel}</span>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="payment-stripe-btn"
              onClick={() => setMethod('stripe')}
              className={`p-3.5 rounded-2xl flex items-center justify-center gap-2 font-extrabold text-xs text-brand-green transition-colors ${method === 'stripe' ? 'border-2 border-brand-orange bg-brand-cream' : 'border-2 border-brand-green/15 bg-white hover:border-brand-orange/50'}`}
            >
              <CreditCard className="w-4 h-4 text-brand-orange" /> {t.order.stripe}
            </button>
            <button
              type="button"
              data-testid="payment-paypal-btn"
              onClick={() => setMethod('paypal')}
              className={`p-3.5 rounded-2xl flex items-center justify-center gap-2 font-extrabold text-xs text-brand-green transition-colors ${method === 'paypal' ? 'border-2 border-brand-orange bg-brand-cream' : 'border-2 border-brand-green/15 bg-white hover:border-brand-orange/50'}`}
            >
              <Wallet className="w-4 h-4 text-brand-orange" /> {t.order.paypal}
            </button>
          </div>
        </div>

        {config.stock !== null && config.stock > 0 && config.stock <= 100 && (
          <div className="flex justify-center" data-testid="stock-badge">
            <span className="inline-flex items-center gap-1.5 bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs font-black uppercase tracking-wider px-4 py-1.5 rounded-full animate-pulse">
              {t.order.stockLeft.replace('{n}', config.stock)}
            </span>
          </div>
        )}

        <div className="bg-brand-cream border-2 border-dashed border-brand-green/20 rounded-2xl px-5 py-4 space-y-2 text-sm" data-testid="order-summary">
          <div className="flex justify-between text-brand-green/80 font-medium">
            <span>{quantity} × Angry Tangerine 237 ml</span>
            <span className="font-bold">{fmt(bundleTotal)}</span>
          </div>
          <div className="flex justify-between text-brand-green/80 font-medium">
            <span>{t.order.shipLabel}</span>
            <span className="font-bold" data-testid="shipping-fee">{shippingEur === 0 ? t.order.freeLabel : fmt(shippingEur)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t-2 border-dashed border-brand-green/20 text-brand-green font-black text-lg">
            <span>{t.order.totalLabel}</span>
            <span data-testid="order-total">{fmt(totalEur)}</span>
          </div>
        </div>

        {showPaypalButtons && (
          <div ref={paypalContainerRef} data-testid="paypal-buttons-container" className="pt-2" />
        )}

        {!showPaypalButtons && (
          <button
            type="submit"
            data-testid="checkout-submit-btn"
            disabled={loading}
            className="w-full py-5 rounded-full bg-brand-orange text-white font-extrabold uppercase tracking-wider hover:bg-[#C75007] hover:scale-[0.98] transition-all shadow-xl citrus-glow text-base disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? t.order.loading : `${t.order.submit} · ${fmt(totalEur)}`}
          </button>
        )}

        <p className="text-center text-[11px] text-brand-green/60 pt-1 inline-flex items-center gap-1 w-full justify-center font-medium">
          <Lock className="w-3 h-3" /> {t.order.secureNote}
        </p>
      </form>
    </div>
  );
};
